import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useOutletContext } from 'react-router-dom';
import api from '../services/api';
import { BACKEND_URL } from '../config';

const BusinessInvoiceView = ({ projectMode = false }) => {
  // Contexte du projet si en mode projet
  const outletContext = useOutletContext();
  const projectId = projectMode ? outletContext?.projectId : null;
  const canManage = projectMode ? outletContext?.canManage : true;
  
  // Base URL de l'API et routes selon le mode
  const apiBase = projectMode && projectId ? `/projects/${projectId}/workspace` : '/business';
  const routeBase = projectMode && projectId ? `/projects/${projectId}/workspace` : '/workspace';
  
  const { id } = useParams();
  const printRef = useRef();
  const [invoice, setInvoice] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, projectId]);

  const loadData = async () => {
    try {
      const [invoiceRes, settingsRes] = await Promise.all([
        api.get(`${apiBase}/invoices/${id}`),
        api.get(`${apiBase}/settings`).catch(() => ({ data: null }))
      ]);
      setInvoice(invoiceRes.data);
      setSettings(settingsRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadInvoice = async () => {
    try {
      const res = await api.get(`${apiBase}/invoices/${id}`);
      setInvoice(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await api.put(`${apiBase}/invoices/${id}`, { statut: newStatus });
      loadInvoice();
    } catch (err) {
      console.error(err);
    }
  };

  const handleValidate = async () => {
    if (!window.confirm(`Valider cette facture ?\n\nCette action va :\n• Changer le statut en "Envoyée"\n• Déduire les quantités du stock des produits`)) return;
    try {
      await api.post(`${apiBase}/invoices/${id}/validate`);
      alert('Facture validée ! Les stocks ont été mis à jour.');
      loadInvoice();
    } catch (err) {
      console.error(err);
      alert('Erreur lors de la validation');
    }
  };

  const getStatusBadge = (statut) => {
    const styles = {
      brouillon: 'bg-gray-100 text-gray-600',
      envoyee: 'bg-blue-100 text-blue-600',
      partiellement_payee: 'bg-amber-100 text-amber-600',
      payee: 'bg-green-100 text-green-600',
      annulee: 'bg-red-100 text-red-600'
    };
    const labels = {
      brouillon: 'Brouillon',
      envoyee: 'Envoyée',
      partiellement_payee: 'Partiellement payée',
      payee: 'Payée',
      annulee: 'Annulée'
    };
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${styles[statut] || styles.brouillon}`}>
        {labels[statut] || statut}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="p-8 flex justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="p-8 text-center">
        <p className="text-red-600">Facture introuvable</p>
        <Link to={`${routeBase}/invoices`} className="text-indigo-600 hover:underline mt-2 block">
          Retour aux factures
        </Link>
      </div>
    );
  }

  const total = parseFloat(invoice.total) || 0;
  const paye = parseFloat(invoice.montant_paye) || 0;
  const reste = total - paye;

  const handlePrint = () => {
    const printContent = printRef.current;
    const originalContents = document.body.innerHTML;
    
    document.body.innerHTML = printContent.innerHTML;
    window.print();
    document.body.innerHTML = originalContents;
    window.location.reload();
  };

  return (
    <div className="min-h-screen bg-gray-100 print:bg-white">
      {/* Header avec actions - Masqué à l'impression */}
      <div className="print:hidden bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to={`${routeBase}/invoices`} className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition">
                ← Retour
              </Link>
              <div>
                <h1 className="text-xl font-bold text-gray-800">{invoice.numero_facture}</h1>
                <p className="text-sm text-gray-500">
                  {invoice.type === 'proforma' ? 'Facture proforma' : 'Facture'} • {new Date(invoice.date_facture).toLocaleDateString('fr-FR')}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {getStatusBadge(invoice.statut)}
              <button
                onClick={handlePrint}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition flex items-center gap-2"
              >
                🖨️ Imprimer
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-6 print:p-0">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 print:block">
          {/* Zone imprimable - Facture */}
          <div className="lg:col-span-2 print:col-span-3" ref={printRef}>
            <div className="bg-white rounded-xl shadow-lg print:shadow-none print:rounded-none overflow-hidden">
              
              {/* En-tête entreprise */}
              <div className="p-8 border-b print:p-6">
                <div className="flex justify-between items-start">
                  {/* Logo et infos entreprise */}
                  <div className="flex items-start gap-4">
                    {settings?.logo && (
                      <img 
                        src={settings.logo.startsWith('http') ? settings.logo : `${BACKEND_URL}${settings.logo}`} 
                        alt="Logo" 
                        className="w-20 h-20 object-contain rounded-lg"
                      />
                    )}
                    <div>
                      <h2 className="text-2xl font-bold text-gray-800">{settings?.nom_entreprise || 'Mon Entreprise'}</h2>
                      {settings?.adresse && <p className="text-gray-600">{settings.adresse}</p>}
                      {settings?.telephone && <p className="text-gray-600">📞 {settings.telephone}</p>}
                      {settings?.email && <p className="text-gray-600">📧 {settings.email}</p>}
                      {settings?.numero_registre && (
                        <p className="text-gray-500 text-sm mt-1">RCCM: {settings.numero_registre}</p>
                      )}
                      {settings?.numero_fiscal && (
                        <p className="text-gray-500 text-sm">NIF: {settings.numero_fiscal}</p>
                      )}
                    </div>
                  </div>
                  
                  {/* Titre et numéro facture */}
                  <div className="text-right">
                    <h1 className="text-3xl font-bold text-indigo-600">
                      {invoice.type === 'proforma' ? 'PROFORMA' : 'FACTURE'}
                    </h1>
                    <p className="text-lg text-gray-600 font-medium">{invoice.numero_facture}</p>
                    <div className="mt-3 text-sm text-gray-500">
                      <p>Date: <span className="text-gray-700 font-medium">{new Date(invoice.date_facture).toLocaleDateString('fr-FR')}</span></p>
                      {invoice.date_echeance && (
                        <p>Échéance: <span className="text-gray-700 font-medium">{new Date(invoice.date_echeance).toLocaleDateString('fr-FR')}</span></p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Informations client */}
              <div className="p-8 bg-gray-50 print:p-6 print:bg-gray-100">
                <div className="flex justify-between">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">Facturé à</h3>
                    <div className="text-gray-800">
                      <p className="text-lg font-semibold">{invoice.client_nom || 'Client'}</p>
                      {invoice.client_adresse && <p className="text-gray-600">{invoice.client_adresse}</p>}
                      {invoice.client_telephone && <p className="text-gray-600">📱 {invoice.client_telephone}</p>}
                      {invoice.client_email && <p className="text-gray-600">📧 {invoice.client_email}</p>}
                    </div>
                  </div>
                  <div className="text-right">
                    {invoice.statut === 'payee' && (
                      <span className="inline-block px-4 py-2 bg-green-100 text-green-700 font-bold rounded-lg text-lg">
                        ✓ PAYÉE
                      </span>
                    )}
                    {invoice.statut === 'partiellement_payee' && (
                      <span className="inline-block px-4 py-2 bg-amber-100 text-amber-700 font-bold rounded-lg">
                        PARTIELLEMENT PAYÉE
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Tableau des lignes */}
              <div className="p-8 print:p-6">
                <table className="w-full">
                  <thead>
                    <tr className="border-b-2 border-gray-200">
                      <th className="text-left pb-3 text-gray-500 font-semibold text-sm uppercase">Description</th>
                      <th className="text-center pb-3 text-gray-500 font-semibold text-sm uppercase w-20">Qté</th>
                      <th className="text-right pb-3 text-gray-500 font-semibold text-sm uppercase w-32">Prix unit.</th>
                      <th className="text-right pb-3 text-gray-500 font-semibold text-sm uppercase w-32">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(invoice.items || []).map((item, index) => (
                      <tr key={index} className="border-b border-gray-100">
                        <td className="py-4 text-gray-800">{item.description}</td>
                        <td className="py-4 text-center text-gray-600">{item.quantite}</td>
                        <td className="py-4 text-right text-gray-600">{parseFloat(item.prix_unitaire).toLocaleString('fr-FR')} XOF</td>
                        <td className="py-4 text-right font-medium text-gray-800">
                          {(parseFloat(item.quantite) * parseFloat(item.prix_unitaire)).toLocaleString('fr-FR')} XOF
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Totaux */}
                <div className="mt-6 flex justify-end">
                  <div className="w-72">
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-gray-600">Sous-total</span>
                      <span className="font-medium">{parseFloat(invoice.sous_total).toLocaleString('fr-FR')} XOF</span>
                    </div>
                    {parseFloat(invoice.tva || 0) > 0 && (
                      <div className="flex justify-between py-2 border-b">
                        <span className="text-gray-600">TVA</span>
                        <span className="font-medium">{parseFloat(invoice.tva).toLocaleString('fr-FR')} XOF</span>
                      </div>
                    )}
                    {parseFloat(invoice.remise || 0) > 0 && (
                      <div className="flex justify-between py-2 border-b text-red-600">
                        <span>Remise</span>
                        <span className="font-medium">-{parseFloat(invoice.remise).toLocaleString('fr-FR')} XOF</span>
                      </div>
                    )}
                    <div className="flex justify-between py-3 bg-indigo-50 px-3 rounded-lg mt-2">
                      <span className="text-xl font-bold text-gray-800">TOTAL</span>
                      <span className="text-xl font-bold text-indigo-600">{total.toLocaleString('fr-FR')} XOF</span>
                    </div>
                    {paye > 0 && (
                      <>
                        <div className="flex justify-between py-2 text-green-600 mt-2">
                          <span>Montant payé</span>
                          <span className="font-medium">-{paye.toLocaleString('fr-FR')} XOF</span>
                        </div>
                        {reste > 0 && (
                          <div className="flex justify-between py-2 text-red-600 font-bold">
                            <span>Reste à payer</span>
                            <span>{reste.toLocaleString('fr-FR')} XOF</span>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Notes et conditions */}
              {(invoice.notes || invoice.conditions || settings?.conditions_paiement || settings?.mentions_legales) && (
                <div className="p-8 bg-gray-50 border-t print:p-6">
                  {invoice.notes && (
                    <div className="mb-4">
                      <h4 className="font-semibold text-gray-700 mb-1">Notes</h4>
                      <p className="text-gray-600 text-sm whitespace-pre-line">{invoice.notes}</p>
                    </div>
                  )}
                  {(invoice.conditions || settings?.conditions_paiement) && (
                    <div className="mb-4">
                      <h4 className="font-semibold text-gray-700 mb-1">Conditions de paiement</h4>
                      <p className="text-gray-600 text-sm whitespace-pre-line">{invoice.conditions || settings.conditions_paiement}</p>
                    </div>
                  )}
                  {settings?.mentions_legales && (
                    <div className="text-xs text-gray-400 mt-4 pt-4 border-t border-gray-200">
                      {settings.mentions_legales}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Historique des paiements - Visible aussi à l'impression */}
            {invoice.payments && invoice.payments.length > 0 && (
              <div className="bg-white rounded-xl shadow-lg mt-6 p-6 print:shadow-none print:mt-4 print:border-t print:rounded-none">
                <h3 className="font-semibold text-gray-800 mb-4">Historique des paiements</h3>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left pb-2 text-gray-500">Date</th>
                      <th className="text-left pb-2 text-gray-500">Mode</th>
                      <th className="text-right pb-2 text-gray-500">Montant</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoice.payments.map((payment, index) => (
                      <tr key={index} className="border-b border-gray-100">
                        <td className="py-2">{new Date(payment.date_paiement).toLocaleDateString('fr-FR')}</td>
                        <td className="py-2">{payment.mode_paiement}</td>
                        <td className="py-2 text-right font-medium text-green-600">
                          {parseFloat(payment.montant).toLocaleString('fr-FR')} XOF
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Sidebar Actions - Masquée à l'impression */}
          <div className="lg:col-span-1 print:hidden">
            <div className="bg-white rounded-xl shadow-lg p-6 sticky top-24">
              {/* État du paiement */}
              <div className="mb-6">
                <h3 className="font-semibold text-gray-800 mb-3">État du paiement</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Total facture</span>
                    <span className="font-medium">{total.toLocaleString('fr-FR')} XOF</span>
                  </div>
                  <div className="flex justify-between text-green-600">
                    <span>Déjà payé</span>
                    <span className="font-medium">{paye.toLocaleString('fr-FR')} XOF</span>
                  </div>
                  {reste > 0 && (
                    <div className="flex justify-between text-red-600 font-bold border-t pt-2">
                      <span>Reste à payer</span>
                      <span>{reste.toLocaleString('fr-FR')} XOF</span>
                    </div>
                  )}
                </div>
                
                {/* Progress bar */}
                <div className="mt-3">
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-green-500 rounded-full transition-all"
                      style={{ width: `${Math.min((paye / total) * 100, 100)}%` }}
                    />
                  </div>
                  <div className="text-xs text-gray-500 mt-1 text-right">
                    {total > 0 ? Math.round((paye / total) * 100) : 0}% payé
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="space-y-3">
                {invoice.statut === 'brouillon' && canManage && (
                  <>
                    <Link
                      to={`${routeBase}/invoices/${id}/edit`}
                      className="w-full py-2 bg-amber-100 text-amber-600 rounded-xl hover:bg-amber-200 transition flex items-center justify-center gap-2"
                    >
                      ✏️ Modifier
                    </Link>
                    <button
                      onClick={handleValidate}
                      className="w-full py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition font-semibold"
                    >
                      ✅ Valider la facture
                    </button>
                    <p className="text-xs text-gray-500 text-center">
                      La validation déduira les produits du stock
                    </p>
                  </>
                )}

                {['envoyee', 'partiellement_payee'].includes(invoice.statut) && reste > 0 && (
                  <button
                    onClick={() => setShowPaymentModal(true)}
                    className="w-full py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 transition font-semibold"
                  >
                    💰 Enregistrer un paiement
                  </button>
                )}

                <button
                  onClick={handlePrint}
                  className="w-full py-2 bg-gray-100 text-gray-600 rounded-xl hover:bg-gray-200 transition"
                >
                  🖨️ Imprimer
                </button>

                {invoice.statut !== 'annulee' && invoice.statut !== 'payee' && (
                  <button
                    onClick={() => {
                      if (window.confirm('Annuler cette facture ?')) {
                        handleStatusChange('annulee');
                      }
                    }}
                    className="w-full py-2 bg-red-50 text-red-600 rounded-xl hover:bg-red-100 transition"
                  >
                    ❌ Annuler la facture
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Modal */}
      {showPaymentModal && (
        <PaymentModal
          invoice={invoice}
          reste={reste}
          apiBase={apiBase}
          onClose={() => setShowPaymentModal(false)}
          onSuccess={() => {
            setShowPaymentModal(false);
            loadInvoice();
          }}
        />
      )}

      {/* Styles d'impression */}
      <style>{`
        @media print {
          @page {
            size: A4;
            margin: 15mm;
          }
          body {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .print\\:hidden {
            display: none !important;
          }
          .print\\:block {
            display: block !important;
          }
          .print\\:col-span-3 {
            grid-column: span 3 / span 3;
          }
          .print\\:shadow-none {
            box-shadow: none !important;
          }
          .print\\:rounded-none {
            border-radius: 0 !important;
          }
          .print\\:p-0 {
            padding: 0 !important;
          }
          .print\\:p-6 {
            padding: 1.5rem !important;
          }
          .print\\:bg-white {
            background-color: white !important;
          }
          .print\\:bg-gray-100 {
            background-color: #f3f4f6 !important;
          }
          .print\\:mt-4 {
            margin-top: 1rem !important;
          }
          .print\\:border-t {
            border-top: 1px solid #e5e7eb !important;
          }
        }
      `}</style>
    </div>
  );
};

// Payment Modal Component
const PaymentModal = ({ invoice, reste, apiBase = '/business', onClose, onSuccess }) => {
  const [form, setForm] = useState({
    montant: reste,
    mode_paiement: 'especes',
    reference: '',
    notes: ''
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`${apiBase}/payments`, {
        invoice_id: invoice.id,
        client_id: invoice.client_id,
        type: 'encaissement',
        ...form
      });
      onSuccess();
    } catch (err) {
      console.error(err);
      alert('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold text-gray-800">Enregistrer un paiement</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
        </div>
        
        <div className="mb-4 p-3 bg-gray-50 rounded-xl">
          <div className="text-sm text-gray-500">Facture {invoice.numero_facture}</div>
          <div className="text-lg font-bold text-red-600">Reste à payer: {reste.toLocaleString()} XOF</div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Montant reçu *</label>
            <input
              type="number"
              min="0"
              max={reste}
              value={form.montant}
              onChange={(e) => setForm({ ...form, montant: e.target.value })}
              className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-indigo-500"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Mode de paiement</label>
            <select
              value={form.mode_paiement}
              onChange={(e) => setForm({ ...form, mode_paiement: e.target.value })}
              className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-indigo-500"
            >
              <option value="especes">💵 Espèces</option>
              <option value="mobile_money">📱 Mobile Money</option>
              <option value="virement">🏦 Virement bancaire</option>
              <option value="cheque">📝 Chèque</option>
              <option value="carte">💳 Carte bancaire</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Référence (optionnel)</label>
            <input
              type="text"
              value={form.reference}
              onChange={(e) => setForm({ ...form, reference: e.target.value })}
              placeholder="N° transaction, chèque..."
              className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows="2"
              className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2 bg-gray-100 text-gray-600 rounded-xl hover:bg-gray-200"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 disabled:opacity-50"
            >
              {saving ? 'Enregistrement...' : '💰 Confirmer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BusinessInvoiceView;
